import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ShieldCheck,
  CheckCircle,
  Clock,
  ArrowRight,
  AlertCircle,
  Loader2,
} from 'lucide-react'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { useAuthStore } from '@personic/shared'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001'

// Framework display names
const FRAMEWORK_NAMES: Record<string, { name: string; fullName: string }> = {
  hipaa: { name: 'HIPAA', fullName: 'Health Insurance Portability and Accountability Act' },
  eeoc: { name: 'EEOC', fullName: 'Equal Employment Opportunity Commission' },
  batson: { name: 'Batson', fullName: 'Batson v. Kentucky Compliance' },
  ferpa: { name: 'FERPA', fullName: 'Family Educational Rights and Privacy Act' },
  finra: { name: 'FINRA', fullName: 'Financial Industry Regulatory Authority' },
}

export default function AttestationConfirmation() {
  const navigate = useNavigate()
  const { attestationData, setStep } = useOnboardingStore()
  const { accessToken } = useAuthStore()
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  useEffect(() => {
    setStep(5)
  }, [setStep])

  const frameworkAttestations = attestationData?.frameworkAttestations || {}
  const completedFrameworks = Object.keys(frameworkAttestations)

  // Submit attestations to backend
  const submitAttestations = async () => {
    setSubmitting(true)
    setErrors([])

    const newErrors: string[] = []

    for (const framework of completedFrameworks) {
      const data = frameworkAttestations[framework]

      try {
        const response = await fetch(`${API_BASE_URL}/attestations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            framework,
            attested_by_name: data.representativeName,
            attested_by_title: data.representativeTitle,
            attested_by_email: data.representativeEmail,
            signature_data: data.signatureData,
            signature_type: data.signatureType,
            attestation_data: {
              acknowledgments: data.acknowledgments,
              submittedAt: data.submittedAt,
            },
          }),
        })

        if (!response.ok) {
          const errorData = await response.json()
          newErrors.push(`${FRAMEWORK_NAMES[framework]?.name || framework}: ${errorData.detail || 'Submission failed'}`)
        }
      } catch (err) {
        newErrors.push(`${FRAMEWORK_NAMES[framework]?.name || framework}: Network error`)
      }
    }

    setSubmitting(false)

    if (newErrors.length > 0) {
      setErrors(newErrors)
    } else {
      setSubmitted(true)
    }
  }

  useEffect(() => {
    // Auto-submit when page loads
    if (completedFrameworks.length > 0 && !submitted && !submitting && accessToken) {
      submitAttestations()
    }
  }, [completedFrameworks.length, submitted, submitting, accessToken])

  const handleContinue = () => {
    navigate('/onboarding/invite-team')
  }

  const handleRetry = () => {
    submitAttestations()
  }

  return (
    <div>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-copper-100 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-copper-600" />
          </div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-midnight-900">
            Attestation Summary
          </h1>
        </div>
        <p className="text-warm-600">
          Review your submitted attestations
        </p>
      </motion.div>

      {/* Status */}
      {submitting && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-6 bg-midnight-50 rounded-xl text-center"
        >
          <Loader2 className="w-12 h-12 mx-auto mb-4 text-copper-500 animate-spin" />
          <p className="text-midnight-700 font-heading font-semibold">
            Submitting attestations...
          </p>
          <p className="text-sm text-midnight-600 mt-1">
            Please wait while we process your submissions.
          </p>
        </motion.div>
      )}

      {/* Errors */}
      {errors.length > 0 && !submitting && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-heading font-semibold text-red-800">
                Some attestations failed to submit
              </p>
              <ul className="mt-2 text-sm text-red-700 space-y-1">
                {errors.map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
              <button
                onClick={handleRetry}
                className="mt-3 text-sm text-red-700 hover:text-red-800 underline"
              >
                Retry submission
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Success Message */}
      {submitted && !submitting && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-6 bg-emerald-50 border border-emerald-200 rounded-xl"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-emerald-800 text-lg">
                Attestations Submitted Successfully
              </h3>
              <p className="text-sm text-emerald-700 mt-1">
                Your regulatory compliance attestations have been submitted and are
                pending review by our compliance team.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Attestation List */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-6"
      >
        <h2 className="text-lg font-heading font-semibold text-midnight-900 mb-4">
          Submitted Attestations
        </h2>
        <div className="space-y-3">
          {completedFrameworks.map((framework, index) => {
            const data = frameworkAttestations[framework]
            const frameworkInfo = FRAMEWORK_NAMES[framework]

            return (
              <motion.div
                key={framework}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * index }}
                className="p-4 bg-white border border-warm-200 rounded-lg"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-midnight-100 flex items-center justify-center flex-shrink-0">
                      <ShieldCheck className="w-5 h-5 text-midnight-600" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-midnight-900">
                        {frameworkInfo?.name || framework.toUpperCase()}
                      </h3>
                      <p className="text-xs text-warm-500">{frameworkInfo?.fullName}</p>
                      <div className="mt-2 text-sm text-warm-600">
                        <p>
                          <span className="font-medium">Representative:</span>{' '}
                          {data.representativeName}
                        </p>
                        <p>
                          <span className="font-medium">Title:</span>{' '}
                          {data.representativeTitle}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-700 rounded-full">
                    <Clock className="w-3 h-3" />
                    <span className="text-xs font-heading">Pending Review</span>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>
      </motion.div>

      {/* What's Next */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mb-8 p-5 bg-midnight-50 rounded-xl"
      >
        <h3 className="font-heading font-semibold text-midnight-900 mb-3">
          What happens next?
        </h3>
        <ul className="text-sm text-midnight-700 space-y-2">
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-midnight-200 flex items-center justify-center flex-shrink-0 text-xs font-heading mt-0.5">
              1
            </span>
            Our compliance team will review your attestations within 1-2 business days.
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-midnight-200 flex items-center justify-center flex-shrink-0 text-xs font-heading mt-0.5">
              2
            </span>
            You'll receive an email notification when your attestations are approved.
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-midnight-200 flex items-center justify-center flex-shrink-0 text-xs font-heading mt-0.5">
              3
            </span>
            Once approved, regulated features for those frameworks will be unlocked.
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-midnight-200 flex items-center justify-center flex-shrink-0 text-xs font-heading mt-0.5">
              4
            </span>
            Attestations are valid for one year and require annual renewal.
          </li>
        </ul>
      </motion.div>

      {/* Continue Button */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <button
          onClick={handleContinue}
          disabled={submitting}
          className="w-full btn btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
        >
          Continue to Team Invitations
          <ArrowRight className="w-4 h-4" />
        </button>
      </motion.div>
    </div>
  )
}
